import { CurrencyPipe, DatePipe } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	inject,
	type OnDestroy,
	type OnInit,
	signal,
	ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { type Table, TableModule, type TablePageEvent } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { Message } from 'primeng/message';
import { ConfirmationService } from 'primeng/api';

import { EFilingHttpService } from '../../services/e-filing-http.service';
import { ToastService } from '../../services/toast.service';

import { Invoice } from '../../models/invoice';

import { environment } from '../../../../environments/environment';
import { EmbarkSpinnerComponent } from '../embark-spinner/embark-spinner.component';
import { EFilingFiltersComponent } from './e-filing-filters/e-filing-filters.component';
import { type EFilingFilters } from '../../models/e-filing-filters';
import { Checkbox, type CheckboxChangeEvent } from 'primeng/checkbox';
import { DecodeHtmlPipe } from '../../pipes/decode-html.pipe';
import * as XLSX from 'xlsx';

import { TimeoutError, type Subscription } from 'rxjs';
import { InvoiceDetailComponent } from './invoice-detail/invoice-detail.component';
import {
	DynamicDialogModule,
	DialogService,
	type DynamicDialogRef
} from 'primeng/dynamicdialog';
import { RequisitionerStore } from '../../stores/requisitioner-store';
import { type BulkActionResponse } from '../../models/bulk-action-response';
import { ParentCompanyStore } from '../../stores/parent-company-store';
import { InvoicePollingService } from '../../services/invoice-polling.service';
import { type InvoicePollResponse } from '../../models/invoice-poll-response';
import { type ValidationError } from '../../models/validation-error';

@Component({
	selector: 'app-e-filing',
	imports: [
		FormsModule,
		DatePipe,
		CurrencyPipe,
		Button,
		ConfirmDialog,
		TableModule,
		DynamicDialogModule,
		Tooltip,
		EmbarkSpinnerComponent,
		EFilingFiltersComponent,
		Checkbox,
		DecodeHtmlPipe,
		Message
	],
	templateUrl: './e-filing.component.html',
	styleUrl: './e-filing.component.sass',
	providers: [ConfirmationService, DatePipe, CurrencyPipe, DialogService]
})
export class EFilingComponent implements OnInit, OnDestroy {
	// public readonly parentCompanies = environment.parentCompanies;

	public isProcessTableLoading = false;
	public isInvoiceRefreshing = false;
	public data: Invoice[] = [];
	public filteredData: Invoice[] = [];
	public selectedInvoices = signal<Invoice[]>([]);
	public paginatorRows = 50;
	public flaggingInvoiceId: string | null = null;
	public flagComment = '';
	public parentCompany: string | null = null;
	public pendingInvoice: Invoice | null = null;
	private lastFilters: EFilingFilters | null = null;

	public allSelected = false;

	public environment = environment;
	private eFilingHttpService = inject<EFilingHttpService>(EFilingHttpService);
	private toastService = inject<ToastService>(ToastService);
	private cdr = inject<ChangeDetectorRef>(ChangeDetectorRef);
	private confirmationService =
		inject<ConfirmationService>(ConfirmationService);
	private datePipe = inject(DatePipe);
	private currencyPipe = inject(CurrencyPipe);
	private readonly requisitionerStore =
		inject<RequisitionerStore>(RequisitionerStore);

	private readonly invoicePollingService = inject<InvoicePollingService>(
		InvoicePollingService
	);
	private pollingSub!: Subscription;

	private readonly parentCompanyStore =
		inject<ParentCompanyStore>(ParentCompanyStore);

	private ref: DynamicDialogRef | null = null;
	private dialogService = inject<DialogService>(DialogService);
	protected readonly defaultSortMeta = [{ field: 'dateObject', order: -1 }];

	@ViewChild('dt') private readonly dt!: Table;

	public ngOnInit(): void {
		this.requisitionerStore.load();
		this.parentCompanyStore.load();

		this.pollingSub = this.invoicePollingService.updates$.subscribe(
			(result: InvoicePollResponse) => {
				const invoices = result.all;
				this.upsertInvoices(invoices);
			}
		);
	}

	public ngOnDestroy(): void {
		this.pollingSub.unsubscribe();
	}

	public onFiltersApplied(filters: EFilingFilters): void {
		this.lastFilters = filters;
		if (
			filters.parentCompany &&
			this.parentCompany !== filters.parentCompany
		) {
			this.parentCompany = filters.parentCompany;
			this.getProcessTableData();
			void this.invoicePollingService.subscribe(this.parentCompany);
		}

		this.filteredData = this.applyFilters(filters);
		this.cdr.detectChanges();
	}

	private applyFilters(filters: EFilingFilters): Invoice[] {
		return this.data.filter((invoice) => {
			const matchesClient =
				filters.client === '' || filters.client === invoice.name;

			const matchesRequisitioner =
				filters.requisitioner === '' ||
				filters.requisitioner === invoice.requisitioner;

			const matchesNsStatus =
				filters.nsStatus === '' ||
				filters.nsStatus === invoice.nsStatus;

			const matchesOtStatus =
				filters.otStatus === 'all' ||
				filters.otStatus === (invoice.otStatus?.toLowerCase() ?? '');

			const matchesOiStatus =
				filters.oiStatus === 'all' ||
				filters.oiStatus === (invoice.oiStatus?.toLowerCase() ?? '');

			const matchesErrorStatus =
				filters.errorStatus.length === 0 ||
				filters.errorStatus.includes(invoice.errorStatus);

			// Ensure invoice dates are properly parsed
			const invoiceStartDate = invoice.invoiceDate
				? new Date(invoice.invoiceDate)
				: null;

			const invoiceEndDate = invoice.invoiceDate
				? new Date(invoice.invoiceDate)
				: null;

			const convertedStartDateFilter = filters.startDate
				? new Date(filters.startDate)
				: null;
			const convertedEndDateFilter = filters.endDate
				? new Date(filters.endDate)
				: null;

			const startDateMatched =
				!convertedStartDateFilter ||
				(invoiceStartDate &&
					invoiceStartDate >= convertedStartDateFilter);
			const endDateMatched =
				!convertedEndDateFilter ||
				(invoiceEndDate && invoiceEndDate <= convertedEndDateFilter);

			const matchesDate = startDateMatched && endDateMatched;

			const haystack: string[] = [
				invoice.po ?? '',
				invoice.ticketNumber,
				invoice.invoiceId,
				invoice.name,
				invoice.requisitioner,
				invoice.assignedTo ?? '',
				invoice.amount.toString(),
				invoice.nsStatus,
				invoice.internalId.toString(),
				invoice.requisitioner,
				invoice.otStatus ?? '',
				invoice.oiStatus ?? '',
				invoice.internalId.toString()
			];

			const needle = filters.search;

			const matchesSearchTerm = !needle.length
				? true
				: haystack.some((string) =>
						string.toLowerCase().includes(needle)
					);

			const reverseSearch = !needle.length
				? true
				: needle.split(' ').includes(invoice.invoiceId);

			return (
				matchesClient &&
				matchesRequisitioner &&
				matchesNsStatus &&
				matchesOtStatus &&
				matchesOiStatus &&
				matchesErrorStatus &&
				(matchesSearchTerm || reverseSearch) &&
				matchesDate
			);
		});
	}

	//updates the paginator when it is clicked/changed
	public handlePaginatorChange($event: TablePageEvent): void {
		this.paginatorRows = $event.rows;

		const start = this.dt.first ?? 0;
		const pageInvoices = this.filteredData.slice(
			start,
			start + this.paginatorRows
		);

		this.allSelected = pageInvoices.every(({ selected }) => selected);
	}

	// Code for submitting PDFs and cXML Invoices

	public onInvoiceSelectionChange(
		event: CheckboxChangeEvent,
		invoice: Invoice
	): void {
		if (event.checked) {
			this.selectedInvoices.update((arr) => [...arr, invoice]);

			if (!this.allSelected) {
				const start = this.dt.first ?? 0;
				const pageInvoices = this.filteredData.slice(
					start,
					start + this.paginatorRows
				);

				if (pageInvoices.every(({ selected }) => selected))
					this.allSelected = true;
			}
		} else {
			this.selectedInvoices.update((arr) =>
				arr.filter(
					({ internalId }) => internalId !== invoice.internalId
				)
			);
			this.allSelected = false;
		}
	}

	public onSelectAll(event: CheckboxChangeEvent): void {
		const start = this.dt.first ?? 0;
		const pageInvoices = this.filteredData.slice(
			start,
			start + this.paginatorRows
		);

		pageInvoices.forEach((invoice) => {
			if (invoice.canSubmit) invoice.selected = Boolean(event.checked);
		});

		const submittablePageInvoices = pageInvoices.filter(
			(invoice) => invoice.canSubmit
		);

		if (event.checked) {
			this.selectedInvoices.update((arr) => [
				...arr,
				...submittablePageInvoices.filter((i) => !arr.includes(i))
			]);
		} else {
			this.selectedInvoices.update((arr) =>
				arr.filter((i) => !submittablePageInvoices.includes(i))
			);
		}
	}

	public onUnselectAll(): void {
		this.allSelected = false;
		this.data.forEach((invoice) => (invoice.selected = false));
		this.selectedInvoices.set([]);
	}

	public exportToExcel(invoice: boolean): void {
		let rows;
		let sheetName;
		let filename;

		if (invoice) {
			rows = this.filteredData.map((invoice) => ({
				PO: invoice.po ?? '',
				SO: invoice.ticketNumber,
				Invoice: invoice.invoiceId,
				Client: invoice.name,
				Requisitioner: this.decodeHtml(invoice.requisitioner),
				'Invoice Date':
					this.datePipe.transform(
						new Date(invoice.date),
						'MM/dd/yyyy'
					) ?? '',
				Amount: this.currencyPipe.transform(invoice.amount) ?? '',
				'NS Status': invoice.nsStatus,
				'OT Status': invoice.otStatus ?? 'N/A',
				'OI Status': invoice.oiStatus ?? 'N/A',
				Errors: invoice.errors?.map((e) => e.message).join('; ') ?? '',
				Warnings:
					invoice.warnings?.map((w) => w.message).join('; ') ?? ''
			}));
			sheetName = 'E-Filing';
			filename = 'e-filing-invoice-export';
		} else {
			rows = Object.values(
				this.requisitionerStore.requisitioners()
			).flatMap((x) => x);
			sheetName = 'Requisitioners';
			filename = 'requisitioners-export';
		}

		const ws = XLSX.utils.json_to_sheet(rows);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, sheetName);
		XLSX.writeFile(wb, `${filename}.xlsx`);
	}

	public invoiceSubmissionHandler(invoice: Invoice): void {
		const blockingErrors = invoice.errors?.filter(
			(e) =>
				!(
					e.code === 'RequisitionerUnmatched' &&
					invoice.enverusRequisitioner
				)
		);

		if (blockingErrors?.length) {
			this.confirmationService.confirm({
				key: 'allErrors',
				message: /*html*/ `
					<div class="invoice-banner invoice-banner--error">
						<i class="pi pi-times-circle"></i>
						${blockingErrors.map((e) => e.message).join('; ')}
					</div>
					Resolve this error before submitting invoice #${invoice.invoiceId}.
				`,
				header: `Cannot Submit Invoice #${invoice.invoiceId}`,
				rejectVisible: false,
				acceptLabel: 'OK',
				acceptButtonStyleClass: 'p-button-secondary'
			});
			return;
		}

		const invoiceDate =
			this.datePipe.transform(new Date(invoice.date), 'MM/dd/yyyy') ??
			invoice.date;
		const amount =
			this.currencyPipe.transform(invoice.amount) ?? invoice.amount;

		let message = /*html*/ `
			<table class="confirm-details-table">
				<tr><td><strong>Invoice #</strong></td><td>${invoice.invoiceId}</td></tr>
				<tr><td><strong>Client</strong></td><td>${invoice.name}</td></tr>
				<tr><td><strong>Invoice Date</strong></td><td>${invoiceDate}</td></tr>
				<tr><td><strong>Amount</strong></td><td>${amount}</td></tr>
				${invoice.po ? `<tr><td><strong>PO Number</strong></td><td>${invoice.po}</td></tr>` : ''}
				<tr><td><strong>Requisitioner</strong></td><td>${this.decodeHtml(invoice.requisitioner)}</td></tr>
				<tr><td><strong>Description</strong></td><td>${invoice.description}</td></tr>
			</table>
		`;

		let titlePrefix = 'Submit';

		const blockingWarnings = this.getBlockingWarnings(invoice);

		if (blockingWarnings?.length) {
			message += /*html*/ `
				<div class="invoice-banner invoice-banner--warning">
					<i class="pi pi-exclamation-triangle"></i>
					${blockingWarnings.map((e) => e.message).join('; ')}
				</div>
			`;
			titlePrefix = 'Save';
		} else {
			this.pendingInvoice = invoice;
			this.confirmationService.confirm({
				key: 'submissionSummaryTwoButton',
				message,
				header: `${titlePrefix} Invoice #${invoice.invoiceId} to Open Ticket`,
				icon: 'pi pi-exclamation-triangle'
			});
			return;
		}

		this.confirmationService.confirm({
			key: 'submissionSummary',
			message,
			header: `${titlePrefix} Invoice #${invoice.invoiceId} to Open Ticket`,
			icon: 'pi pi-exclamation-triangle',
			rejectButtonStyleClass: 'p-button-text',
			accept: () => {
				this.submitInvoices([invoice]);
			}
		});
	}

	public invoicesSubmissionHandler(): void {
		const invoices = this.selectedInvoices();
		const errored = invoices.filter((i) => i.errors?.length);
		const valid = invoices.filter((i) => !i.errors?.length);

		if (errored.length === invoices.length) {
			const rows = errored
				.map(
					(i) => /*html*/ `
					<tr>
						<td><strong>#${i.invoiceId}</strong></td>
						<td>${i.name}</td>
						<td>
							<div class="invoice-banner invoice-banner--error" style="margin:0">
								<i class="pi pi-times-circle"></i>
								${i.errors?.map((e) => e.message).join('; ') ?? ''}
							</div>
						</td>
					</tr>`
				)
				.join('');

			this.confirmationService.confirm({
				key: 'allErrors',
				message: /*html*/ `
					<p>All selected invoices have errors and cannot be submitted. Resolve the errors below before submitting.</p>
					<table class="confirm-details-table">
						<thead><tr><th>Invoice</th><th>Client</th><th>Error</th></tr></thead>
						<tbody>${rows}</tbody>
					</table>
				`,
				header: 'Cannot Submit — All Invoices Have Errors',
				rejectVisible: false,
				acceptLabel: 'OK',
				acceptButtonStyleClass: 'p-button-secondary'
			});
			return;
		}

		if (errored.length > 0) {
			const rows = errored
				.map(
					(i) => /*html*/ `
					<tr>
						<td><strong>#${i.invoiceId}</strong></td>
						<td>${i.name}</td>
						<td>
							<div class="invoice-banner invoice-banner--error" style="margin:0">
								<i class="pi pi-times-circle"></i>
								${i.errors?.map((e) => e.message).join('; ') ?? ''}
							</div>
						</td>
					</tr>`
				)
				.join('');

			this.confirmationService.confirm({
				key: 'someErrors',
				message: /*html*/ `
					<p>
						${errored.length} invoice${errored.length > 1 ? 's' : ''} cannot be submitted due to errors.
						Would you like to proceed with the remaining <strong>${valid.length}</strong> valid invoice${valid.length > 1 ? 's' : ''}?
					</p>
					<table class="confirm-details-table">
						<thead><tr><th>Invoice</th><th>Client</th><th>Error</th></tr></thead>
						<tbody>${rows}</tbody>
					</table>
				`,
				header: `${errored.length} Invoice${errored.length > 1 ? 's' : ''} Have Errors`,
				icon: 'pi pi-exclamation-triangle',
				acceptLabel: `Proceed with ${valid.length} valid invoice${valid.length > 1 ? 's' : ''}`,
				rejectLabel: 'Cancel',
				rejectButtonStyleClass: 'p-button-text',
				accept: () => {
					errored.forEach((invoice) => (invoice.selected = false));
					this.selectedInvoices.set(valid);
					this.acceptBulkSubmission(valid);
				}
			});
			return;
		}

		this.acceptBulkSubmission(valid);
	}

	private acceptBulkSubmission(valid: Invoice[]): void {
		const hasWarnings = valid.some((i) => i.warnings?.length);
		const rows = valid
			.map((i) => {
				const invoiceDate =
					this.datePipe.transform(new Date(i.date), 'MM/dd/yyyy') ??
					i.date;
				const amount =
					this.currencyPipe.transform(i.amount) ?? i.amount;

				const warningCell = i.warnings?.length
					? /*html*/ `
							<div class="invoice-banner invoice-banner--warning" style="margin:0">
								<i class="pi pi-exclamation-triangle"></i>
								${i.warnings.map((e) => e.message).join('; ')}
							</div>`
					: '';

				return /*html*/ `
					<tr>
						<td><strong>#${i.invoiceId}</strong></td>
						<td>${i.name}</td>
						<td>${invoiceDate}</td>
						<td>${amount}</td>
						${hasWarnings ? `<td>${warningCell}</td>` : ''}
					</tr>`;
			})
			.join('');

		const warningHeader = hasWarnings ? `<th>Warning</th>` : '';

		this.confirmationService.confirm({
			key: 'submissionSummary',
			message: /*html*/ `
				<p>Review the ${valid.length} invoice${valid.length > 1 ? 's' : ''} below and confirm you are ready to submit them to Open Invoice.</p>
				<table class="confirm-details-table">
					<thead><tr><th>Invoice</th><th>Client</th><th>Date</th><th>Amount</th>${warningHeader}</tr></thead>
					<tbody>${rows}</tbody>
				</table>
			`,
			header: `Submit ${valid.length} Invoice${valid.length > 1 ? 's' : ''} to Open Ticket`,
			icon: 'pi pi-exclamation-triangle',
			rejectButtonStyleClass: 'p-button-text',
			accept: () => {
				this.submitInvoices(valid);
			}
		});
	}

	private getProcessTableData(): void {
		this.isProcessTableLoading = true;
		this.eFilingHttpService
			.getEFilingData(this.parentCompany ?? '')
			.subscribe({
				next: (res: Invoice[]) => {
					this.data = res.map((invoice) =>
						Object.assign(new Invoice(), invoice, {
							dateObject: new Date(invoice.invoiceDate)
						})
					);

					this.filteredData = this.data;
					this.isProcessTableLoading = false;
				},
				error: () => {
					this.toastService.showErrorToast(
						'Error loading process table data'
					);
					this.isProcessTableLoading = false;
				}
			});
	}

	public submitInvoiceDestinationOverride(destination: string): void {
		if (!this.pendingInvoice) return;
		this.submitInvoices([this.pendingInvoice], destination);
		this.confirmationService.close();
	}

	private submitInvoices(
		invoices: Invoice[],
		destination: string | null = null
	): void {
		invoices.forEach((invoice) => {
			invoice.isSubmitting = true;
		});

		if (invoices.length !== 1) destination = null;
		this.eFilingHttpService
			.submitInvoices(invoices, destination)
			.subscribe({
				next: (updates: BulkActionResponse[]) => {
					const errored = updates.filter((u) => u.error != null);

					const successes = updates.filter((u) => u.error == null);

					successes.forEach((update) => {
						try {
							const data = update.data as Invoice[];
							const invoice = this.data.find(
								({ invoiceId }) => invoiceId == update.value
							);

							if (!invoice)
								throw new Error('could not find invoice');

							if (data[0]) {
								const updated = Object.fromEntries(
									Object.entries(data[0]).filter(
										([, value]) =>
											value !== undefined &&
											value !== null
									)
								);
								Object.assign(invoice, updated);
							}
						} catch (e) {
							console.error('unable to update invoice: ', e);
							console.log('unable to update invoice', update);
						}
					});

					const successTotal = successes.length;

					if (successTotal > 0) {
						this.toastService.showSuccessToast(
							`${successTotal} invoice${successTotal > 1 ? 's' : ''} submitted successfully.`
						);
					}

					if (errored.length > 0) {
						const rows = errored
							.map(
								(u) => /*html*/ `
                            <tr>
                                <td><strong>#${u.value as string}</strong></td>
                                <td>
                                    <div class="invoice-banner invoice-banner--error" style="margin:0">
                                        <i class="pi pi-times-circle"></i>
                                        ${u.error ?? ''}
                                    </div>
                                </td>
                            </tr>`
							)
							.join('');

						this.confirmationService.confirm({
							key: 'errorResponse',
							message: /*html*/ `
                            <p>${errored.length} invoice${errored.length > 1 ? 's' : ''} could not be submitted.</p>
                            <table class="confirm-details-table">
                                <thead><tr><th>Invoice</th><th>Error</th></tr></thead>
                                <tbody>${rows}</tbody>
                            </table>
                        `,
							header: `Submission Error${errored.length > 1 ? 's' : ''}`,
							rejectVisible: false,
							acceptLabel: 'OK',
							acceptButtonStyleClass: 'p-button-secondary'
						});
					}
					this.onUnselectAll();
				},
				error: () => {
					this.toastService.showErrorToast(
						'Failed to submit invoices.'
					);
					invoices.forEach((invoice) => {
						invoice.isSubmitting = false;
					});
				},
				complete: () => {
					invoices.forEach((invoice) => {
						invoice.isSubmitting = false;
					});
				}
			});
	}

	private decodeHtml(value: string): string {
		const doc = new DOMParser().parseFromString(value, 'text/html');
		return doc.body.textContent;
	}

	public viewInvoice(invoice: Invoice): void {
		if (invoice.isSubmitting) return;
		this.ref?.close();
		this.ref = this.dialogService.open(InvoiceDetailComponent, {
			header: invoice.invoiceNumber,
			width: '75%',
			modal: true,
			closable: true,
			closeOnEscape: true,
			dismissableMask: true,
			data: {
				invoice,
				onSubmit: (inv: Invoice) => {
					this.invoiceSubmissionHandler(inv);
				}
			}
		});
	}

	public openTicketUrl(invoice: Invoice): void {
		if (invoice.openTicketUrl) {
			window.open(invoice.openTicketUrl, '_blank');
		}
	}

	public openInvoiceUrl(invoice: Invoice): void {
		if (invoice.openInvoiceUrl) {
			window.open(invoice.openInvoiceUrl, '_blank');
		}
	}

	private upsertInvoices(incoming: Invoice[]): void {
		incoming.forEach((updated) => {
			const existing = this.data.find(
				(i) => i.internalId === updated.internalId
			);
			if (existing) {
				Object.assign(existing, updated);
			} else {
				this.data.push(
					Object.assign(new Invoice(), updated, {
						dateObject: new Date(updated.invoiceDate)
					})
				);
			}
		});
		if (this.lastFilters) {
			this.filteredData = this.applyFilters(this.lastFilters);
		} else {
			this.filteredData = [...this.data];
		}
		this.cdr.detectChanges();
	}

	public onRefresh(): void {
		this.isInvoiceRefreshing = true;
		this.eFilingHttpService.refreshInvoices().subscribe({
			next: (succeeded) => {
				this.isInvoiceRefreshing = false;
				if (!succeeded) {
					this.toastService.showErrorToast(
						'Unable to refresh invoices'
					);
					return;
				}

				this.toastService.showSuccessToast('Invoices Refreshed');
				this.getProcessTableData();
			},
			error: (err: { status?: number }) => {
				this.isInvoiceRefreshing = false;
				const timedOut =
					err instanceof TimeoutError || err.status === 504;
				this.toastService.showErrorToast(
					timedOut
						? 'Invoice refresh timed out. Please try again.'
						: 'Unable to refresh invoices'
				);
			}
		});
	}

	public ticketComplianceTooltip(invoice: Invoice): string {
		return (
			invoice.ticketComplianceErrors
				?.map((e) => e.message)
				.filter((message) => !!message)
				.join('; ') ?? ''
		);
	}

	public get showBuyerTestingBanner(): boolean {
		if (!this.parentCompany || this.parentCompanyStore.loading())
			return false;

		return (
			this.parentCompanyStore
				.parentCompanies()
				.find((pc) => pc.name === this.parentCompany)?.isTesting ??
			false
		);
	}

	public get parentCompanyLabel(): string {
		if (!this.parentCompany || this.parentCompanyStore.loading()) return '';

		return (
			this.parentCompanyStore
				.parentCompanies()
				.find((pc) => pc.name === this.parentCompany)?.label ?? ''
		);
	}

	private getBlockingWarnings(
		invoice: Invoice
	): ValidationError[] | undefined {
		if (invoice.parentCompany !== 'permian') return invoice.warnings;

		const badRequisitioners = ['tom cockrell', 'armando arredondo'];

		return invoice.warnings?.filter(
			(e) =>
				!(
					e.code === 'RequisitionerUnmatched' &&
					invoice.enverusRequisitioner &&
					!badRequisitioners.includes(
						invoice.enverusRequisitioner.toLowerCase()
					)
				)
		);
	}
}
